import React, { useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import FormField from '../components/FormField';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './ProductScheduleEditor.module.css';
import { getRoleAccessState, ROLE_ARTIST } from '../utils/roles';
import { formatPrice, formatProductSaleStatus } from '../utils/marketplace';
import { timezoneLabel } from '../utils/currency';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import { getCompactCollectibleImagePath } from '../utils/collectibleMedia';

function toDateTimeLocalValue(value) {
  if (!value?.toDate) return '';
  const date = value.toDate();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export default function ProductScheduleEditor() {
  const { productId } = useParams();
  const appUser = useContext(UserContext);
  const artistAccess = getRoleAccessState(appUser, ROLE_ARTIST);
  const connectStatus = deriveActiveConnectStatus(appUser);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({
    saleStartAt: '',
    saleEndAt: '',
    status: 'inactive',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'products', String(productId)));
        if (!active) return;
        if (!snap.exists()) {
          setError('Collectible not found.');
          setProduct(null);
          return;
        }
        const data = { id: snap.id, ...(snap.data() || {}) };
        if (String(data.artistOwnerUid || '') !== String(appUser?.id || '')) {
          setError('Only the artist owner can edit this collectible schedule.');
          setProduct(null);
          return;
        }
        if (data.dropId) {
          setError('Drop-backed products lock after publish. Open the drop detail page instead.');
          setProduct(null);
          return;
        }
        setProduct(data);
        setForm({
          saleStartAt: toDateTimeLocalValue(data.saleStartAt),
          saleEndAt: toDateTimeLocalValue(data.saleEndAt),
          status: String(data.status || 'inactive'),
        });
        setError('');
      } catch (err) {
        if (active) {
          setProduct(null);
          setError(err?.message || 'Unable to load collectible.');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.id, productId]);

  const statusLabel = useMemo(() => formatProductSaleStatus(product || {}), [product]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!artistAccess.isActive) {
      setError('An active Artist subscription is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const callable = httpsCallable(functions, 'updateProduct');
      await callable({
        productId,
        updates: {
          saleStartAt: form.saleStartAt,
          saleEndAt: form.saleEndAt,
          status: form.status,
        },
      });
      navigate(`/products/${productId}`, { state: { backTo: '/creator/products' } });
    } catch (err) {
      setError(err?.message || 'Unable to update collectible schedule.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate(`/products/${productId}`, { state: { backTo: '/creator/products' } })} />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Collectible Schedule</span>
          <h1>Edit Schedule</h1>
          <p>Post-create edits are limited to the sale window. Price, artwork, title, supply, and currency stay locked after creation.</p>
        </header>
        {!connectStatus.canPayout ? <p className={styles.notice}>Stripe payouts are not fully enabled yet. You can still activate this collectible, but your creator share will stay held until setup is complete.</p> : null}

        {loading ? <p className={styles.notice}>Loading collectible…</p> : null}
        {!loading && error && !product ? <p className={styles.error}>{error}</p> : null}

        {!loading && product ? (
          <div className={styles.layout}>
            <section className={styles.summaryCard}>
              <div className={styles.mediaWrap}>
                <ResolvedStorageImage path={getCompactCollectibleImagePath(product)} alt={product.title || 'Collectible'} fallback={<div className={styles.placeholder}>Collectible</div>} />
              </div>
              <div className={styles.summaryCopy}>
                <span className={styles.statusPill}>{statusLabel}</span>
                <h2>{product.title || product.id}</h2>
                <p>{product.description || 'No description added yet.'}</p>
                <div className={styles.lockedGrid}>
                  <div><span>Price</span><strong>{formatPrice(product)}</strong></div>
                  <div><span>Supply</span><strong>{product.maxSupply || product.inventory?.maxSupply || 0}</strong></div>
                  <div><span>Timezone</span><strong>{timezoneLabel(product.timezone || 'UTC')}</strong></div>
                  <div><span>Editable Now</span><strong>Schedule only</strong></div>
                </div>
              </div>
            </section>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formIntro}>
                <h3>Sale Controls</h3>
                <p>Changes apply to the collectible schedule and live status without reopening the full create form.</p>
              </div>
              <div className={styles.grid}>
                <FormField label="Sale Start">
                  <input value={form.saleStartAt} onChange={(event) => setForm((prev) => ({ ...prev, saleStartAt: event.target.value }))} type="datetime-local" required />
                </FormField>
                <FormField label="Sale End">
                  <input value={form.saleEndAt} onChange={(event) => setForm((prev) => ({ ...prev, saleEndAt: event.target.value }))} type="datetime-local" required />
                </FormField>
                <FormField label="Status">
                  <select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}>
                    <option value="inactive">Inactive</option>
                    <option value="active">Active</option>
                  </select>
                </FormField>
              </div>
              {error ? <p className={styles.error}>{error}</p> : null}
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={() => navigate(`/products/${productId}`, { state: { backTo: '/creator/products' } })}>Cancel</button>
                <button type="submit" className={styles.primaryButton} disabled={saving || !artistAccess.isActive}>{saving ? 'Saving…' : 'Save Schedule'}</button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
}
